import {
  Datatable,
  DatatableBody,
  DatatableHead,
  DatatableHeader,
  DatatableRow,
} from '@monksflow/monks-ui';
import { useTranslation } from 'react-i18next';
import { SortableHead } from '@/shared/ui/data-table';
import { TABLE_COL_WIDTH, TABLE_NAME_COL_NO_SIDEBAR } from '@/shared/constants';
import { AudiencesTableV2Row } from './AudiencesTableV2Row';
import { AudiencesTableV2SkeletonRow } from './AudiencesTableV2SkeletonRow';
import type { AudiencesTableV2Props } from './AudiencesTableV2.types';

const SKELETON_ROWS = Array.from({ length: 5 }, (_, index) => index);

export function AudiencesTableV2({
  personas,
  gwiEnabled,
  isLoading,
  sortBy,
  sortDirection,
  onSort,
  onEdit,
  onDelete,
}: AudiencesTableV2Props) {
  const { t } = useTranslation('audiences');

  // A background refetch keeps the rows on screen, so only a cold load is busy.
  const isBusy = isLoading && personas.length === 0;
  const showActions = Boolean(onEdit ?? onDelete);

  return (
    <Datatable aria-busy={isBusy || undefined} className="[&_thead_th]:border-b-2">
      <DatatableHeader>
        <DatatableRow>
          <SortableHead
            column="personaTitle"
            label={t('audiences.audiencesTableV2.name')}
            className={TABLE_NAME_COL_NO_SIDEBAR}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <DatatableHead scope="col">{t('audiences.audiencesTableV2.traits')}</DatatableHead>
          <DatatableHead scope="col" className={TABLE_COL_WIDTH.type}>
            {t('audiences.audiencesTableV2.topic')}
          </DatatableHead>
          <SortableHead
            column="creationStatus"
            label={t('audiences.audiencesTableV2.status')}
            className={TABLE_COL_WIDTH.statusBadge}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          <SortableHead
            column="updated"
            label={t('audiences.audiencesTableV2.lastRefreshed')}
            className={TABLE_COL_WIDTH.dateText}
            sortBy={sortBy}
            sortDirection={sortDirection}
            onSort={onSort}
          />
          {showActions && (
            <DatatableHead scope="col" className={TABLE_COL_WIDTH.actions}>
              <span className="sr-only">{t('audiences.audiencesTableV2.actions')}</span>
            </DatatableHead>
          )}
        </DatatableRow>
      </DatatableHeader>
      <DatatableBody>
        {isBusy
          ? SKELETON_ROWS.map((index) => (
              <AudiencesTableV2SkeletonRow key={index} showActions={showActions} />
            ))
          : personas.map((persona) => (
              <AudiencesTableV2Row
                key={persona.id}
                persona={persona}
                gwiEnabled={gwiEnabled}
                showActions={showActions}
                onEdit={onEdit}
                onDelete={onDelete}
              />
            ))}
      </DatatableBody>
    </Datatable>
  );
}

AudiencesTableV2.displayName = 'AudiencesTableV2';
